import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserCog } from 'lucide-react';

const roles = ['admin', 'cajero', 'mesero'];
const roleLabels = { admin: 'Administrador', cajero: 'Cajero', mesero: 'Mesero' };
const roleStyles = { admin: 'bg-purple-100 text-purple-800', cajero: 'bg-blue-100 text-blue-800', mesero: 'bg-orange-100 text-orange-800' };

export default function StaffPage() {
  const { API } = useAuth();
  const [staff, setStaff] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'mesero' });

  const fetchStaff = useCallback(() => {
    axios.get(`${API}/staff`).then(r => setStaff(r.data)).catch(() => toast.error('Error al cargar personal'));
  }, [API]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const openCreate = () => { setEditing(null); setForm({ name: '', email: '', password: '', role: 'mesero' }); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, email: s.email, password: '', role: s.role }); setDialogOpen(true); };

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error('Nombre y email son requeridos'); return; }
    if (!editing && !form.password) { toast.error('Contrasena es requerida'); return; }
    try {
      if (editing) {
        const data = { name: form.name, email: form.email, role: form.role };
        if (form.password) data.password = form.password;
        await axios.put(`${API}/staff/${editing.id}`, data);
        toast.success('Personal actualizado');
      } else {
        await axios.post(`${API}/staff`, form);
        toast.success('Personal creado');
      }
      setDialogOpen(false);
      fetchStaff();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/staff/${id}`);
      toast.success('Personal eliminado');
      fetchStaff();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Personal</h1>
          <p className="text-stone-500 text-sm mt-1">{staff.length} miembros del equipo</p>
        </div>
        <Button data-testid="add-staff-btn" onClick={openCreate} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-6 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="staff-table">
            <thead>
              <tr className="bg-stone-50/50">
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Nombre</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Email</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Rol</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Registro</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.id} className="border-t border-stone-100 hover:bg-stone-50/50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-stone-200 rounded-full flex items-center justify-center">
                        <span className="text-stone-600 text-xs font-semibold">{s.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="text-sm font-medium text-stone-900">{s.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-stone-600">{s.email}</td>
                  <td className="p-4"><Badge className={`${roleStyles[s.role]} border-0 rounded-full text-xs px-2.5 py-0.5`}>{roleLabels[s.role]}</Badge></td>
                  <td className="p-4 text-sm text-stone-500">{s.created_at ? new Date(s.created_at).toLocaleDateString('es-MX') : '-'}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-staff-${s.id}`} onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-orange-700 transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button data-testid={`delete-staff-${s.id}`} onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-stone-500 hover:text-red-700 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-400 text-sm">No hay personal registrado</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Editar Personal' : 'Nuevo Usuario'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nombre</Label><Input data-testid="staff-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="staff-form-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" /></div>
            <div>
              <Label>{editing ? 'Nueva Contrasena (dejar vacio para no cambiar)' : 'Contrasena'}</Label>
              <Input data-testid="staff-form-password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-form-role" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map(r => <SelectItem key={r} value={r}>{roleLabels[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="staff-form-submit" onClick={handleSubmit} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
